import { useTranslation } from "react-i18next";
import { useThemeClasses } from "../providers/ThemeProvider";
import type {
  LiveShiftData,
  LiveStatusTransition,
  TopStatusData,
  TopRejectData,
  TopStatus,
  TopReject,
} from "../hooks/useLiveShiftData";
import { useMemo, useEffect, useRef, useCallback } from "react";
import { animate, stagger } from "animejs";
import { isMachineRunning } from "../utils/machineStatus";

const cx = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(" ");
};

interface LiveShiftProps {
  machineId?: string;
  machineName?: string;
  shiftData?: LiveShiftData | null;
  lastTransition?: LiveStatusTransition | null;
  topStops?: TopStatusData | null;
  topRejects?: TopRejectData | null;
  loading?: boolean;
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function formatMTBF(seconds: number | null): string {
  if (!seconds || seconds === 0) return "--";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatTimeUTC(isoString: string): string {
  const d = new Date(isoString);
  return `${d.getUTCHours().toString().padStart(2, "0")}:${d.getUTCMinutes().toString().padStart(2, "0")}`;
}

function formatTimeLocal(isoString: string): string {
  const d = new Date(isoString);
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}:${d.getSeconds().toString().padStart(2, "0")}`;
}

// Performance level helpers
function getPerformanceLevel(pct: number): {
  label: string;
  color: string;
  bgColor: string;
  emoji: string;
} {
  if (pct < 0)
    return {
      label: "Warming Up",
      color: "text-blue-400",
      bgColor: "from-blue-400/10 to-blue-400/5",
      emoji: "⏳",
    };
  if (pct >= 90)
    return {
      label: "Excellent",
      color: "text-emerald-500",
      bgColor: "from-emerald-500/10 to-emerald-500/5",
      emoji: "🔥",
    };
  if (pct >= 75)
    return {
      label: "Good",
      color: "text-blue-500",
      bgColor: "from-blue-500/10 to-blue-500/5",
      emoji: "👍",
    };
  if (pct >= 50)
    return {
      label: "Average",
      color: "text-amber-500",
      bgColor: "from-amber-500/10 to-amber-500/5",
      emoji: "⚡",
    };
  if (pct >= 25)
    return {
      label: "Below Target",
      color: "text-orange-500",
      bgColor: "from-orange-500/10 to-orange-500/5",
      emoji: "⚠️",
    };
  return {
    label: "Critical",
    color: "text-red-500",
    bgColor: "from-red-500/10 to-red-500/5",
    emoji: "🚨",
  };
}

function getGaugeGradientId(pct: number): string {
  if (pct >= 75) return "gaugeGreen";
  if (pct >= 50) return "gaugeBlue";
  if (pct >= 25) return "gaugeAmber";
  return "gaugeRed";
}

// Calculate shift time progress
// Shift times are stored as UTC but represent local times, so compare with local time
function getShiftProgress(start: string, end: string): number {
  const nowLocal = new Date();
  const now = Date.UTC(
    nowLocal.getFullYear(),
    nowLocal.getMonth(),
    nowLocal.getDate(),
    nowLocal.getHours(),
    nowLocal.getMinutes(),
    nowLocal.getSeconds(),
  );
  const startMs = new Date(start).getTime();
  let endMs = new Date(end).getTime();
  // Handle overnight shifts (e.g. 22:00–06:00)
  if (endMs <= startMs) {
    endMs += 24 * 60 * 60 * 1000;
  }
  if (now < startMs) return 0;
  if (now > endMs) return 100;
  return ((now - startMs) / (endMs - startMs)) * 100;
}

/** Animated number that smoothly transitions between values */
const AnimatedNumber = ({
  value,
  decimals = 0,
  className,
  suffix = "",
}: {
  value: number;
  decimals?: number;
  className?: string;
  suffix?: string;
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const prevValue = useRef(value);

  useEffect(() => {
    if (!ref.current || prevValue.current === value) return;
    const obj = { val: prevValue.current };
    animate(obj, {
      val: value,
      duration: 800,
      ease: "outExpo",
      onUpdate: () => {
        if (ref.current) {
          ref.current.textContent = obj.val.toFixed(decimals) + suffix;
        }
      },
    });
    // Flash effect on change
    animate(ref.current, {
      scale: [1.1, 1],
      duration: 400,
      ease: "outElastic(1, .6)",
    });
    prevValue.current = value;
  }, [value, decimals, suffix]);

  return (
    <span ref={ref} className={className}>
      {value.toFixed(decimals)}
      {suffix}
    </span>
  );
};

/** Pulse animation on element when a dependency value changes */
function usePulse() {
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useCallback(() => {
    if (!ref.current) return;
    animate(ref.current, {
      boxShadow: [
        "0 0 0 0 rgba(16,185,129,0.4)",
        "0 0 0 12px rgba(16,185,129,0)",
      ],
      duration: 600,
      ease: "outQuad",
    });
  }, []);
  return { ref, trigger };
}

const LiveShift = ({
  shiftData = null,
  lastTransition = null,
  topStops = null,
  topRejects = null,
  loading = false,
}: LiveShiftProps) => {
  const { t } = useTranslation();
  const tc = useThemeClasses();
  const containerRef = useRef<HTMLDivElement>(null);
  const gaugeRef = useRef<SVGCircleElement>(null);
  const outputPulse = usePulse();

  // Pulse output card only when output value actually changes
  const prevOutput = useRef<number | null>(null);
  useEffect(() => {
    if (!shiftData) return;
    const output = shiftData.kpis.good_products ?? 0;
    if (prevOutput.current !== null && prevOutput.current !== output) {
      outputPulse.trigger();
    }
    prevOutput.current = output;
  }, [shiftData, outputPulse]);

  // Animate gauge arc only when completion % changes
  const prevCompletion = useRef<number | null>(null);
  useEffect(() => {
    if (!gaugeRef.current || !shiftData) return;
    const completionPct = shiftData.kpis.completion_pct ?? 0;
    if (prevCompletion.current === completionPct) return;
    prevCompletion.current = completionPct;
    const circumference = 2 * Math.PI * 80;
    const targetOffset =
      circumference * (1 - Math.min(completionPct, 100) / 100);
    animate(gaugeRef.current, {
      strokeDashoffset: targetOffset,
      duration: 1200,
      ease: "inOutQuart",
    });
  }, [shiftData]);

  // Stagger entrance animation on first data load only
  const hasAnimated = useRef(false);
  useEffect(() => {
    if (!containerRef.current || !shiftData || hasAnimated.current) return;
    hasAnimated.current = true;
    const cards = containerRef.current.querySelectorAll("[data-animate]");
    animate(cards, {
      opacity: [0, 1],
      translateY: [20, 0],
      delay: stagger(80),
      duration: 600,
      ease: "outQuad",
    });
  }, [shiftData]);

  const shiftProgress = useMemo(() => {
    if (!shiftData) return 0;
    return getShiftProgress(shiftData.shift.start, shiftData.shift.end);
  }, [shiftData]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full"></div>
          </div>
          <span className="text-gray-500 font-medium">
            {t("liveShiftConnecting") || "Connecting live data..."}
          </span>
        </div>
      </div>
    );
  }

  if (!shiftData) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="text-5xl mb-4 animate-bounce">📡</div>
        <p className={cx(tc.textSecondary, "text-lg font-medium")}>
          {t("noLiveData") || "Waiting for live data..."}
        </p>
        <p className={cx(tc.textMuted, "text-sm mt-1")}>
          No active shift data stream detected
        </p>
      </div>
    );
  }

  const { shift, kpis, timestamp } = shiftData;
  const top_status = topStops?.items || [];
  const top_reject = topRejects?.items || [];
  // Downtime = total stop time this shift: the sum of every (non-running) stop
  // status, i.e. the total of the "Top Stops" panel. Kept in sync with that list
  // so the KPI and the breakdown match (includes internal + external/knock-on +
  // material/idle stops — anything where the machine wasn't running).
  const totalDowntimeSec = top_status
    .filter((s) => !isMachineRunning(s.name, s.code))
    .reduce((acc, s) => acc + (s.duration_seconds || 0), 0);
  const completionPct = kpis.completion_pct !== null ? kpis.completion_pct : 0;
  const efficiencyPct =
    kpis.machine_efficiency !== null ? kpis.machine_efficiency * 100 : 0;
  const utilizationPct =
    kpis.utilization_ratio !== null ? kpis.utilization_ratio * 100 : 0;
  // Performance relative to elapsed shift time
  // Don't judge until at least 15% of shift has elapsed (ramp-up grace period)
  // Also stay in warm-up if utilization is very low (machine was mostly stopped)
  const expectedPct = shiftProgress > 0 ? shiftProgress : 1;
  const isWarmingUp = shiftProgress < 15 || utilizationPct < 10;
  const relativePerformance = isWarmingUp
    ? -1
    : (completionPct / expectedPct) * 100;
  const perfLevel = getPerformanceLevel(relativePerformance);

  // Calculate a "shift score" combining relative performance, efficiency, and uptime
  const scorePerformance = relativePerformance < 0 ? 50 : relativePerformance; // Neutral during warm-up
  const shiftScore = Math.round(
    Math.min(
      100,
      scorePerformance * 0.4 + efficiencyPct * 0.35 + utilizationPct * 0.25,
    ),
  );

  // Hover hints explaining how the Shift Score and on-pace % are calculated,
  // using the current live values so the explanation is concrete.
  const shiftScoreHint = [
    "Shift Score (0–100) — weighted average of three KPIs:",
    `• Pace ×40% = ${(scorePerformance * 0.4).toFixed(0)} (${isWarmingUp ? "warm-up → neutral 50" : `${scorePerformance.toFixed(0)}%`})`,
    `• Efficiency ×35% = ${(efficiencyPct * 0.35).toFixed(0)} (${efficiencyPct.toFixed(0)}%)`,
    `• Utilization ×25% = ${(utilizationPct * 0.25).toFixed(0)} (${utilizationPct.toFixed(0)}%)`,
    `Total (capped at 100) = ${shiftScore}`,
  ].join("\n");

  const onPaceHint = isWarmingUp
    ? [
        "On pace — warming up.",
        "Pace isn't scored until 15% of the shift has elapsed and utilization ≥ 10%.",
        `Shift elapsed: ${shiftProgress.toFixed(0)}% • Utilization: ${utilizationPct.toFixed(0)}%`,
      ].join("\n")
    : [
        "On pace = production progress vs. time elapsed:",
        `(completion ${completionPct.toFixed(0)}% ÷ shift elapsed ${shiftProgress.toFixed(0)}%) × 100 = ${relativePerformance.toFixed(0)}%`,
        "100% = exactly on pace • >100% ahead • <100% behind",
      ].join("\n");

  return (
    <div ref={containerRef} className="p-4 md:p-6 space-y-5 min-h-full">
      {/* Header Banner */}
      <div
        data-animate
        className={cx(
          "rounded-2xl p-4 md:p-5 relative overflow-hidden",
          "bg-gradient-to-r from-[#025a94] to-[#0284c7]",
          "shadow-lg shadow-blue-900/20",
        )}
      >
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/20 -translate-y-1/2 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white/10 translate-y-1/2 -translate-x-1/4"></div>
        </div>

        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div
                className={cx(
                  "w-3 h-3 rounded-full shadow-lg",
                  isMachineRunning(
                    lastTransition?.transition.to_name,
                    lastTransition?.transition.to,
                  )
                    ? "bg-green-400 animate-pulse shadow-green-400/50"
                    : "bg-amber-400 shadow-amber-400/50",
                )}
              />
              <span className="text-white font-bold text-lg">
                {shift.designation}
              </span>
            </div>
            <div className="hidden md:flex items-center gap-2 text-white/70 text-sm">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {formatTimeUTC(shift.start)}–{formatTimeUTC(shift.end)}
            </div>
            {lastTransition && (
              <div
                className={cx(
                  "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold",
                  isMachineRunning(
                    lastTransition.transition.to_name,
                    lastTransition.transition.to,
                  )
                    ? "bg-emerald-400/20 text-emerald-200"
                    : "bg-amber-400/20 text-amber-200",
                )}
              >
                {isMachineRunning(
                  lastTransition.transition.to_name,
                  lastTransition.transition.to,
                ) ? (
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                ) : (
                  <span>⏸</span>
                )}
                <span>{lastTransition.transition.to_name}</span>
                <span className="text-white/40 font-normal ml-1">
                  {formatTimeLocal(lastTransition.transition.at)}
                </span>
              </div>
            )}
          </div>
          <div className="text-right text-white/80 text-xs">
            <div className="font-medium">
              {new Date(shift.date).toLocaleDateString()}
            </div>
            <div className="text-white/50 mt-0.5">
              Updated {formatTimeLocal(timestamp)}
            </div>
          </div>
        </div>

        {/* Shift time progress bar */}
        <div className="relative mt-4">
          <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-400 to-emerald-300 rounded-full transition-all duration-1000"
              style={{ width: `${shiftProgress}%` }}
            />
          </div>
          <div className="flex justify-between mt-1.5 text-[0.65rem] text-white/50">
            <span>Shift start</span>
            <span>{shiftProgress.toFixed(0)}% elapsed</span>
            <span>Shift end</span>
          </div>
        </div>
      </div>

      {/* Main Grid: Score + KPIs */}
      <div data-animate className="grid grid-cols-12 gap-5">
        {/* Left: Big Radial Score */}
        <div
          className={cx(
            "col-span-12 lg:col-span-4 rounded-2xl p-6 flex flex-col items-center justify-center",
            "bg-white dark:bg-slate-800",
            "border border-gray-100 dark:border-slate-700",
            "shadow-sm dark:shadow-lg dark:shadow-black/20",
          )}
        >
          {/* Circular gauge */}
          <div className="relative w-48 h-48">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 200 200">
              <defs>
                <linearGradient
                  id="gaugeGreen"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#10b981" />
                  <stop offset="100%" stopColor="#34d399" />
                </linearGradient>
                <linearGradient
                  id="gaugeBlue"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#60a5fa" />
                </linearGradient>
                <linearGradient
                  id="gaugeAmber"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#f59e0b" />
                  <stop offset="100%" stopColor="#fbbf24" />
                </linearGradient>
                <linearGradient id="gaugeRed" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#ef4444" />
                  <stop offset="100%" stopColor="#f87171" />
                </linearGradient>
              </defs>
              {/* Background track */}
              <circle
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke="currentColor"
                className="text-gray-100 dark:text-slate-700"
                strokeWidth="16"
              />
              {/* Progress arc */}
              <circle
                ref={gaugeRef}
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke={`url(#${getGaugeGradientId(relativePerformance < 0 ? 50 : relativePerformance)})`}
                strokeWidth="16"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 80}`}
                strokeDashoffset={`${2 * Math.PI * 80}`}
              />
            </svg>
            {/* Center content */}
            <div
              title={onPaceHint}
              className="absolute inset-0 flex flex-col items-center justify-center cursor-help"
            >
              <span className="text-2xl">{perfLevel.emoji}</span>
              <AnimatedNumber
                value={completionPct}
                decimals={1}
                suffix="%"
                className={cx(
                  tc.textPrimary,
                  "text-3xl font-black tabular-nums mt-1",
                )}
              />
              <span
                className={cx(
                  perfLevel.color,
                  "text-xs font-bold uppercase tracking-wider mt-1",
                )}
              >
                {perfLevel.label}
              </span>
              {relativePerformance >= 0 && (
                <span className={cx(tc.textMuted, "text-[0.6rem] mt-0.5")}>
                  {relativePerformance.toFixed(0)}% on pace
                </span>
              )}
            </div>
          </div>

          {/* Production stats below gauge */}
          <div
            ref={outputPulse.ref}
            className="w-full mt-4 space-y-2 rounded-lg"
          >
            <div className="flex justify-between items-center px-2">
              <span className={cx(tc.textMuted, "text-xs font-medium")}>
                {t("target")}
              </span>
              <span
                className={cx(tc.textPrimary, "text-sm font-bold tabular-nums")}
              >
                {kpis.target ?? "--"}
              </span>
            </div>
            <div className="flex justify-between items-center px-2">
              <span className={cx(tc.textMuted, "text-xs font-medium")}>
                {t("output")}
              </span>
              <AnimatedNumber
                value={kpis.good_products ?? 0}
                className="text-sm font-bold tabular-nums text-emerald-600 dark:text-emerald-400"
              />
            </div>
            <div className="flex justify-between items-center px-2">
              <span className={cx(tc.textMuted, "text-xs font-medium")}>
                {t("rejects")}
              </span>
              <AnimatedNumber
                value={kpis.waste ?? 0}
                className="text-sm font-bold tabular-nums text-red-500"
              />
            </div>
          </div>
        </div>

        {/* Right: KPI Tiles */}
        <div className="col-span-12 lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-4">
          <MetricTile
            icon="⚡"
            title={t("speed")}
            value={kpis.current_speed !== null ? `${kpis.current_speed}` : "--"}
            unit="ppm"
            target={
              kpis.nominal_speed !== null ? `${kpis.nominal_speed}` : undefined
            }
            gauge={{
              value: kpis.current_speed ?? 0,
              max: kpis.nominal_speed || kpis.current_speed || 1,
            }}
            tc={tc}
          />
          <MetricTile
            icon="🎯"
            title={t("efficiency")}
            value={`${efficiencyPct.toFixed(1)}`}
            unit="%"
            progress={efficiencyPct}
            tc={tc}
          />
          <MetricTile
            icon="⏱️"
            title={t("utilization")}
            value={`${utilizationPct.toFixed(1)}`}
            unit="%"
            progress={utilizationPct}
            tc={tc}
          />
          <MetricTile
            icon="🔧"
            title="MTBF"
            value={formatMTBF(kpis.mtbf_seconds)}
            unit="min"
            tc={tc}
          />
          <MetricTile
            icon="🛑"
            title={t("downtime", "Downtime")}
            value={
              totalDowntimeSec > 0 ? formatDuration(totalDowntimeSec) : "--"
            }
            tc={tc}
          />
          {/* Shift Score */}
          <div
            title={shiftScoreHint}
            className={cx(
              "rounded-2xl p-4 flex flex-col items-center justify-center text-center relative overflow-hidden cursor-help",
              "bg-gradient-to-br from-violet-500/10 to-purple-500/5 dark:from-violet-500/20 dark:to-purple-500/10",
              "border border-violet-200/50 dark:border-violet-500/30",
            )}
          >
            <span className="text-2xl mb-1">
              {shiftScore >= 75
                ? "🏆"
                : shiftScore >= 50
                  ? "📊"
                  : shiftScore >= 25
                    ? "📉"
                    : "⚠️"}
            </span>
            <div className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wide">
              Shift Score
            </div>
            <AnimatedNumber
              value={shiftScore}
              className={cx(
                tc.textPrimary,
                "text-3xl font-black tabular-nums mt-1",
              )}
            />
            <div className="text-[0.6rem] text-violet-500/70 dark:text-violet-400/70 mt-0.5">
              / 100
            </div>
          </div>
        </div>
      </div>

      {/* Top Stops & Rejects as modern lists */}
      <div data-animate className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top Stops */}
        <div
          className={cx(
            "rounded-2xl overflow-hidden",
            "bg-white dark:bg-slate-800",
            "border border-gray-100 dark:border-slate-700",
            "shadow-sm dark:shadow-lg dark:shadow-black/20",
          )}
        >
          <div className="px-5 pt-4 pb-2 flex items-center gap-2">
            <span className="text-base">🛑</span>
            <h4 className={cx(tc.textPrimary, "font-bold text-sm")}>
              {t("topStops")}
            </h4>
          </div>
          <div className="px-5 pb-4 space-y-2">
            {top_status.filter(
              (s: TopStatus) => s.duration_seconds > 0 || s.quantity > 0,
            ).length === 0 ? (
              <div className={cx(tc.textMuted, "py-6 text-center text-sm")}>
                <span className="text-2xl block mb-2">✨</span>
                {t("noStops")} — Great job!
              </div>
            ) : (
              top_status
                .filter(
                  (s: TopStatus) => s.duration_seconds > 0 || s.quantity > 0,
                )
                .sort((a: TopStatus, b: TopStatus) => b.quantity - a.quantity)
                .slice(0, 10)
                .map((stop: TopStatus, idx: number) => {
                  const maxQty = top_status.reduce(
                    (max, s) => Math.max(max, s.quantity),
                    0,
                  );
                  const barPct =
                    maxQty > 0 ? (stop.quantity / maxQty) * 100 : 0;
                  return (
                    <div
                      key={idx}
                      className="relative rounded-lg overflow-hidden"
                    >
                      {/* Background bar */}
                      <div
                        className="absolute inset-0 bg-red-50 dark:bg-red-500/25 rounded-lg transition-all duration-500"
                        style={{ width: `${barPct}%` }}
                      />
                      <div className="relative flex items-center justify-between px-3 py-2.5">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span
                            className={cx(
                              "flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[0.6rem] font-bold",
                              "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400",
                            )}
                          >
                            {idx + 1}
                          </span>
                          <span
                            className={cx(tc.textPrimary, "text-sm truncate")}
                          >
                            {stop.name || `Status ${stop.code}`}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                          <span
                            className={cx(tc.textMuted, "text-xs tabular-nums")}
                          >
                            {formatDuration(stop.duration_seconds)}
                          </span>
                          <span
                            className={cx(
                              "text-xs font-bold tabular-nums px-2 py-0.5 rounded-full",
                              "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
                            )}
                          >
                            ×{stop.quantity}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </div>

        {/* Top Rejects */}
        <div
          className={cx(
            "rounded-2xl overflow-hidden",
            "bg-white dark:bg-slate-800",
            "border border-gray-100 dark:border-slate-700",
            "shadow-sm dark:shadow-lg dark:shadow-black/20",
          )}
        >
          <div className="px-5 pt-4 pb-2 flex items-center gap-2">
            <span className="text-base">📦</span>
            <h4 className={cx(tc.textPrimary, "font-bold text-sm")}>
              {t("topRejects")}
            </h4>
          </div>
          <div className="px-5 pb-4 space-y-2">
            {!top_reject || top_reject.length === 0 ? (
              <div className={cx(tc.textMuted, "py-6 text-center text-sm")}>
                {kpis.waste && kpis.waste > 0 ? (
                  <>
                    <span className="text-2xl block mb-2">📦</span>
                    {kpis.waste} total rejects — no breakdown available
                  </>
                ) : (
                  <>
                    <span className="text-2xl block mb-2">🎉</span>
                    {t("noRejects")} — Perfect!
                  </>
                )}
              </div>
            ) : (
              top_reject
                .sort((a: TopReject, b: TopReject) => b.quantity - a.quantity)
                .slice(0, 10)
                .map((reject: TopReject, idx: number) => {
                  const maxQty = top_reject.reduce(
                    (max, r) => Math.max(max, r.quantity),
                    0,
                  );
                  const barPct =
                    maxQty > 0 ? (reject.quantity / maxQty) * 100 : 0;
                  return (
                    <div
                      key={idx}
                      className="relative rounded-lg overflow-hidden"
                    >
                      <div
                        className="absolute inset-0 bg-orange-50 dark:bg-orange-500/25 rounded-lg transition-all duration-500"
                        style={{ width: `${barPct}%` }}
                      />
                      <div className="relative flex items-center justify-between px-3 py-2.5">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span
                            className={cx(
                              "flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[0.6rem] font-bold",
                              "bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-400",
                            )}
                          >
                            {idx + 1}
                          </span>
                          <span
                            className={cx(tc.textPrimary, "text-sm truncate")}
                          >
                            {reject.cause || `Code ${reject.reject_no}`}
                          </span>
                        </div>
                        <span
                          className={cx(
                            "text-xs font-bold tabular-nums px-2 py-0.5 rounded-full flex-shrink-0 ml-2",
                            "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400",
                          )}
                        >
                          {reject.quantity}
                        </span>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* Modern Metric Tile */
interface MetricTileProps {
  icon: string;
  title: string;
  value: string;
  unit?: string;
  target?: string;
  progress?: number;
  /**
   * When set, renders a banded speedometer gauge instead of the value + bar.
   * `value` is the current reading, `max` is the 100% reference (e.g. nominal
   * speed); the dial scale runs to 110% of `max`.
   */
  gauge?: { value: number; max: number };
  tc: ReturnType<typeof useThemeClasses>;
}

// Colored bands of the speedometer, expressed as a percentage of the target.
const GAUGE_BANDS: { from: number; to: number; color: string }[] = [
  { from: 0, to: 40, color: "#e23b52" },
  { from: 40, to: 60, color: "#ef8f5b" },
  { from: 60, to: 80, color: "#f5c542" },
  { from: 80, to: 100, color: "#a8cbe2" },
  { from: 100, to: 110, color: "#2f5d8e" },
];
const GAUGE_LABELS = [0, 40, 60, 80, 100, 110];
const GAUGE_SCALE_MAX = 110; // dial goes a little past 100%
// Sweep geometry (screen coords, angle clockwise from +x, y pointing down):
// 0% sits at the lower-left, the arc rises over the top, 110% ends lower-right.
const GAUGE_START_DEG = 180;
const GAUGE_SWEEP_DEG = 180;

/** Banded speedometer gauge with a tapered needle. */
const BandedGauge = ({
  value,
  max,
  tc,
}: {
  value: number;
  max: number;
  tc: ReturnType<typeof useThemeClasses>;
}) => {
  const CX = 100;
  const CY = 96;
  const R = 84;
  const strokeW = 15;
  const pct = max > 0 ? (value / max) * 100 : 0;
  const polar = (deg: number, r: number): [number, number] => {
    const rad = (deg * Math.PI) / 180;
    return [CX + r * Math.cos(rad), CY + r * Math.sin(rad)];
  };
  const degFor = (p: number) =>
    GAUGE_START_DEG + (p / GAUGE_SCALE_MAX) * GAUGE_SWEEP_DEG;
  const bandPath = (from: number, to: number) => {
    const [x1, y1] = polar(degFor(from), R);
    const [x2, y2] = polar(degFor(to), R);
    const large = degFor(to) - degFor(from) > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2}`;
  };
  // Needle
  const nDeg = degFor(Math.min(Math.max(pct, 0), GAUGE_SCALE_MAX));
  const nRad = (nDeg * Math.PI) / 180;
  const dir: [number, number] = [Math.cos(nRad), Math.sin(nRad)];
  const perp: [number, number] = [-dir[1], dir[0]];
  const needleLen = R - 20;
  const baseHalf = 6;
  const tip: [number, number] = [
    CX + dir[0] * needleLen,
    CY + dir[1] * needleLen,
  ];
  const bL: [number, number] = [
    CX + perp[0] * baseHalf,
    CY + perp[1] * baseHalf,
  ];
  const bR: [number, number] = [
    CX - perp[0] * baseHalf,
    CY - perp[1] * baseHalf,
  ];
  return (
    <div className="relative w-full">
      <svg viewBox="0 4 200 102" className="w-full">
        {/* Colored bands */}
        {GAUGE_BANDS.map((b, i) => (
          <path
            key={i}
            d={bandPath(b.from, b.to)}
            fill="none"
            stroke={b.color}
            strokeWidth={strokeW}
          />
        ))}
        {/* Tick labels (inside the arc) */}
        {GAUGE_LABELS.map((lbl) => {
          const [lx, ly] = polar(degFor(lbl), R - strokeW - 10);
          const [t1x, t1y] = polar(degFor(lbl), R - strokeW / 2 - 1);
          const [t2x, t2y] = polar(degFor(lbl), R - strokeW / 2 - 5);
          return (
            <g key={lbl}>
              <line
                x1={t1x}
                y1={t1y}
                x2={t2x}
                y2={t2y}
                stroke="currentColor"
                strokeWidth={1}
                className="text-gray-400 dark:text-slate-500"
              />
              <text
                x={lx}
                y={ly}
                textAnchor="middle"
                dominantBaseline="middle"
                className={tc.textMuted}
                style={{ fontSize: 9 }}
                fill="currentColor"
              >
                {lbl}%
              </text>
            </g>
          );
        })}
        {/* Tapered needle */}
        <polygon
          points={`${tip[0]},${tip[1]} ${bL[0]},${bL[1]} ${bR[0]},${bR[1]}`}
          className={cx(tc.textPrimary, "transition-all duration-700 ease-out")}
          fill="currentColor"
        />
        <circle
          cx={CX}
          cy={CY}
          r={baseHalf}
          className={tc.textPrimary}
          fill="currentColor"
        />
      </svg>
    </div>
  );
};

const MetricTile = ({
  icon,
  title,
  value,
  unit,
  target,
  progress,
  gauge,
  tc,
}: MetricTileProps) => (
  <div
    className={cx(
      "rounded-2xl p-4 flex flex-col",
      gauge ? "justify-start" : "justify-between",
      "bg-white dark:bg-slate-800",
      "border border-gray-100 dark:border-slate-700",
      "shadow-sm dark:shadow-lg dark:shadow-black/20",
      "hover:shadow-md hover:border-blue-200 dark:hover:border-blue-600/40 transition-all duration-200",
    )}
  >
    <div className="flex items-center gap-2 mb-3">
      <span className="text-lg">{icon}</span>
      <span
        className={cx(
          tc.textMuted,
          "text-xs font-semibold uppercase tracking-wide",
        )}
      >
        {title}
      </span>
    </div>
    {gauge ? (
      <div className="flex items-center gap-2">
        <div className="flex flex-col justify-center flex-shrink-0">
          <div
            className={cx(tc.textPrimary, "text-2xl font-black tabular-nums")}
          >
            {value}
            {unit && (
              <span className={cx(tc.textMuted, "text-sm font-normal ml-0.5")}>
                {unit}
              </span>
            )}
          </div>
          {target && (
            <div className={cx(tc.textMuted, "text-[0.65rem] mt-1")}>
              Target: {target}
              {unit}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <BandedGauge value={gauge.value} max={gauge.max} tc={tc} />
        </div>
      </div>
    ) : (
      <>
        <div className={cx(tc.textPrimary, "text-2xl font-black tabular-nums")}>
          {value}
          {unit && (
            <span className={cx(tc.textMuted, "text-sm font-normal ml-0.5")}>
              {unit}
            </span>
          )}
        </div>
        {target && (
          <div className={cx(tc.textMuted, "text-[0.65rem] mt-1")}>
            Target: {target}
            {unit}
          </div>
        )}
        {progress !== undefined && (
          <div className="mt-2.5">
            <div className="h-1.5 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className={cx(
                  "h-full rounded-full transition-all duration-700 ease-out",
                  progress >= 75
                    ? "bg-gradient-to-r from-emerald-400 to-emerald-500"
                    : progress >= 50
                      ? "bg-gradient-to-r from-blue-400 to-blue-500"
                      : progress >= 25
                        ? "bg-gradient-to-r from-amber-400 to-amber-500"
                        : "bg-gradient-to-r from-red-400 to-red-500",
                )}
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>
        )}
      </>
    )}
  </div>
);

export default LiveShift;
